import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { api, errMsg } from "../lib/api";
import MyntraCommandCenter from "./MyntraCommandCenter";
import {
  CheckCircle2,
  XCircle,
  PackageCheck,
  PackageOpen,
  RefreshCcw,
  Webhook,
  ShoppingBag,
  RotateCcw,
  Copy,
  Activity,
  Link2,
  Warehouse as WarehouseIcon,
  Gauge,
  AlertTriangle,
  ListChecks,
  LayoutDashboard as LayoutDashboardIcon,
  IndianRupee,
  Clock,
  PackagePlus,
} from "lucide-react";

const STATUS_TONE = {
  created:    "bg-amber-500/15 text-amber-300 border-amber-500/30",
  shipped:    "bg-sky-500/15 text-sky-300 border-sky-500/30",
  delivered:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelled:  "bg-rose-500/15 text-rose-300 border-rose-500/30",
  updated:    "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
};

function StatusPill({ value }) {
  const cls = STATUS_TONE[value] || "bg-white/[0.04] text-white/65 border-white/10";
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold border ${cls} uppercase tracking-wider`}>
      {value || "—"}
    </span>
  );
}

function copy(value, label = "Copied") {
  navigator.clipboard.writeText(value);
  toast.success(label);
}

const WEBHOOK_PATHS = [
  ["order.create",     "/api/myntra/webhooks/order/create"],
  ["order.cancel",     "/api/myntra/webhooks/order/cancel"],
  ["order.shipped",    "/api/myntra/webhooks/order/shipped"],
  ["order.delivered",  "/api/myntra/webhooks/order/delivered"],
  ["return.create",    "/api/myntra/webhooks/return/create"],
  ["return.update",    "/api/myntra/webhooks/return/update"],
  ["inventory.failed", "/api/myntra/webhooks/inventory/failed"],
];

export default function MyntraHub() {
  // Support ?tab= deep-link (used by CommandCenter quick-actions & widgets)
  const initialTab = (() => {
    if (typeof window === "undefined") return "dashboard";
    const p = new URLSearchParams(window.location.search).get("tab");
    return p || "dashboard";
  })();
  const [tab, setTab] = useState(initialTab);
  const [me, setMe] = useState(null);
  const [status, setStatus] = useState(null);
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [events, setEvents] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [syncData, setSyncData] = useState(null);
  const [apiLogs, setApiLogs] = useState([]);
  const [tiles, setTiles] = useState(null);
  const [listings, setListings] = useState([]);
  const [lowStock, setLowStock] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  async function load() {
    setRefreshing(true);
    try {
      const [s, o, r, e, wh, sy, lg, t, ls, low, aud] = await Promise.all([
        api.get("/seller/myntra/status/full"),
        api.get("/seller/marketplace/myntra/orders?limit=100"),
        api.get("/seller/myntra/returns?limit=50"),
        api.get("/seller/myntra/events?limit=25"),
        api.get("/seller/marketplace/myntra/warehouses"),
        api.get("/seller/marketplace/myntra/sync-status"),
        api.get("/seller/marketplace/myntra/api-logs?limit=50"),
        api.get("/seller/marketplace/myntra/dashboard-tiles"),
        api.get("/seller/marketplace/myntra/listings?limit=100"),
        api.get("/seller/marketplace/myntra/inventory/low-stock?threshold=5"),
        api.get("/seller/marketplace/myntra/inventory/audit-log?limit=50"),
      ]);
      setStatus(s.data);
      setOrders(o.data?.items || []);
      setReturns(r.data?.items || []);
      setEvents(e.data?.items || []);
      setWarehouses(wh.data?.warehouses || []);
      setSyncData(sy.data);
      setApiLogs(lg.data?.logs || []);
      setTiles(t.data);
      setListings(ls.data?.listings || []);
      setLowStock(low.data);
      setAuditLog(aud.data?.items || []);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }
  useEffect(() => {
    load();
    // Real-time polling every 15s
    const t = setInterval(() => load(), 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // Fetch profile for Command Center greeting (best-effort — silent on 401)
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  async function onRetrySync(syncType) {
    try {
      await api.post(`/seller/marketplace/myntra/sync/${syncType}/retry`);
      toast.success(`Retry queued for ${syncType}`);
      setTimeout(load, 1500);
    } catch (err) {
      toast.error(errMsg(err, "Retry failed"));
    }
  }

  // Per-SKU publish (P2 wire-up on Listings tab)
  async function onPublishSku(listing) {
    try {
      await api.post("/seller/marketplace/myntra/publish", {
        skus: [{ sku: listing.sku, price: listing.price || 0 }],
        warehouse_code: listing.warehouse_code || undefined,
      });
      toast.success(`Publish queued for ${listing.sku}`);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Publish failed"));
    }
  }

  // Bulk inventory submit (P3)
  async function onSubmitInventory(rows) {
    try {
      const { data } = await api.post("/seller/marketplace/myntra/inventory/bulk", { rows });
      toast.success(`${data.count} SKU(s) updated — ${data.status}`);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Inventory update failed"));
    }
  }

  // Command Center replaces the classic dashboard entirely. Only the
  // detail tabs (sync/listings/inventory/warehouses/orders/returns/events)
  // fall through to the classic wrapper.
  if (tab === "dashboard") {
    return (
      <AppShell>
        <MyntraCommandCenter userInfo={me} />
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 pb-8">
          <div className="mt-2 pt-4 border-t border-white/[0.06] flex items-center gap-1 overflow-x-auto">
            <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold mr-2">
              Deep dive →
            </span>
            {[
              ["sync", "Sync"],
              ["listings", "Listings"],
              ["warehouses", "Warehouses"],
              ["orders", "Orders"],
              ["returns", "Returns"],
              ["events", "Events log"],
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className="text-[11px] font-bold uppercase tracking-wider text-white/60 hover:text-white px-3 py-1.5 rounded-lg hover:bg-white/[0.05] transition-colors whitespace-nowrap"
                data-testid={`ccc-deep-${id}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="myntra-hub-page">
        {/* Hero */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#4F46E5] font-bold">Marketplace integration</div>
            <h1 className="text-2xl font-bold mt-1 text-white">Myntra Hub</h1>
            <p className="text-sm text-white/55 mt-1 max-w-2xl">
              Bidirectional integration — orders, returns and inventory sync.
              All 32 Mockify APIs + 7 inbound webhooks live.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={refreshing}
              className="px-3 py-1.5 rounded-md border border-white/10 text-xs font-bold text-white/70 hover:border-[#4F46E5] hover:text-[#4F46E5] disabled:opacity-50 transition-colors flex items-center gap-1.5"
              data-testid="myntra-hub-refresh"
            >
              <RefreshCcw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <Link
              to="/seller/profile"
              className="px-3 py-1.5 rounded-md bg-[#4F46E5] text-white text-xs font-bold hover:bg-[#4338CA] transition-colors flex items-center gap-1.5"
              data-testid="myntra-hub-connect"
            >
              <Link2 size={12} /> {status?.connected ? "Manage Connection" : "Connect Myntra"}
            </Link>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            icon={status?.connected ? CheckCircle2 : XCircle}
            label="Connection"
            value={status?.connected ? "Connected" : "Not connected"}
            tone={status?.connected ? "emerald" : "rose"}
          />
          <KpiCard
            icon={ShoppingBag}
            label="Orders mirrored"
            value={status?.orders_mirrored ?? 0}
            tone="violet"
          />
          <KpiCard
            icon={RotateCcw}
            label="Returns mirrored"
            value={status?.returns_mirrored ?? 0}
            tone="amber"
          />
          <KpiCard
            icon={Webhook}
            label="Token days left"
            value={status?.token_days_left ?? "—"}
            tone="sky"
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-white/[0.08] overflow-x-auto">
          {[
            ["dashboard",  "Dashboard",         LayoutDashboardIcon],
            ["sync",       "Sync",              Gauge],
            ["listings",   "Listings",          PackagePlus],
            ["warehouses", "Warehouses",        WarehouseIcon],
            ["orders",     "Orders",            ShoppingBag],
            ["returns",    "Returns",           RotateCcw],
            ["events",     "Events log",        Webhook],
          ].map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5 border-b-2 whitespace-nowrap ${
                tab === id
                  ? "text-white border-[#4F46E5]"
                  : "text-white/55 border-transparent hover:text-white/85"
              }`}
              data-testid={`myntra-hub-tab-${id}`}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>

        {loading && <div className="text-sm text-white/55">Loading Myntra data…</div>}

        {!loading && tab === "dashboard" && <DashboardTab tiles={tiles} />}
        {!loading && tab === "sync" && (
          <SyncTab data={syncData} logs={apiLogs} onRetry={onRetrySync} />
        )}
        {!loading && tab === "listings" && (
          <ListingsTab listings={listings} onPublish={onPublishSku} onRefresh={load} />
        )}
        {!loading && tab === "warehouses" && <WarehousesTab warehouses={warehouses} />}
        {!loading && tab === "status" && (
          <StatusTab status={status} base={base} />
        )}
        {!loading && tab === "orders"   && <OrdersTab orders={orders} onRefresh={load} />}
        {!loading && tab === "returns"  && <ReturnsTab returns={returns} />}
        {!loading && tab === "events"   && <EventsTab events={events} />}
        {!loading && tab === "actions"  && <ActionsTab />}
      </div>
    </AppShell>
  );
}

function KpiCard({ icon: Icon, label, value, tone = "violet" }) {
  const toneCls = {
    violet:  "text-[#A5B4FC]",
    emerald: "text-emerald-300",
    rose:    "text-rose-300",
    amber:   "text-amber-300",
    sky:     "text-sky-300",
  }[tone];
  return (
    <div className="p-4 rounded-xl bg-white/[0.025] border border-white/10">
      <div className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
        <Icon size={11} className={toneCls} /> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${toneCls} tabular-nums`}>{value}</div>
    </div>
  );
}

function StatusTab({ status, base }) {
  return (
    <div className="space-y-4">
      <div className="p-5 rounded-xl bg-slate-900 border border-slate-700 shadow-lg">
        <div className="text-xs uppercase tracking-widest text-white font-bold mb-3">
          Webhook endpoints — share these with Myntra Partner Console
        </div>
        <div className="space-y-2">
          {WEBHOOK_PATHS.map(([name, path]) => (
            <div key={path} className="flex items-center justify-between gap-3 p-3 rounded-md bg-slate-950 border border-slate-700">
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-bold mb-1">{name}</div>
                <div className="text-sm font-mono text-white truncate font-semibold" style={{ wordBreak: 'break-all' }}>
                  {base}{path}
                </div>
              </div>
              <button
                onClick={() => copy(`${base}${path}`, `${name} URL copied`)}
                className="shrink-0 px-3 py-1.5 rounded-md bg-[#4F46E5] hover:bg-[#4338CA] text-white text-xs font-bold flex items-center gap-1.5 transition-colors"
                data-testid={`copy-webhook-${name}`}
              >
                <Copy size={11} /> Copy
              </button>
            </div>
          ))}
        </div>
        <div className="text-xs text-white/80 mt-3 p-3 rounded-md bg-slate-950 border border-slate-700">
          <span className="font-bold text-white">Public probe: </span>
          <span className="font-mono text-emerald-400 font-semibold" style={{ wordBreak: 'break-all' }}>
            {base}/api/myntra/webhooks/health
          </span>
          <div className="text-white/70 mt-1">Myntra can hit this to confirm reachability before going live.</div>
        </div>
      </div>

      {status?.last_event && (
        <div className="p-5 rounded-xl bg-slate-900 border border-slate-700">
          <div className="text-xs uppercase tracking-widest text-white font-bold mb-3">Last received event</div>
          <div className="text-sm">
            <span className="font-bold text-emerald-400">{status.last_event.event_type}</span>
            <span className="text-white/70 ml-2 font-mono">{status.last_event.received_at}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersTab({ orders, onRefresh }) {
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [busySku, setBusySku] = useState(null);
  const filtered = orders.filter((o) => {
    if (statusFilter && o.status !== statusFilter) return false;
    if (query && !(o.seller_order_id || "").toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  async function runAction(orderId, action, extra = {}) {
    setBusySku(`${orderId}:${action}`);
    try {
      const { data } = await api.post(
        `/seller/marketplace/myntra/order/${encodeURIComponent(orderId)}/action`,
        { action, ...extra }
      );
      toast.success(`${action} → ${data.new_status || data.status || "ok"}`);
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(errMsg(err, `${action} failed`));
    } finally {
      setBusySku(null);
    }
  }

  function openPdf(orderId, kind) {
    const url = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/seller/marketplace/myntra/order/${encodeURIComponent(orderId)}/${kind}.pdf`;
    const token = localStorage.getItem("token") || "";
    // Open the PDF in a new tab with the auth token appended via fetch → blob URL
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r)))
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
      })
      .catch(() => toast.error(`Failed to open ${kind}`));
  }

  async function onExportCsv() {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const qs = params.toString();
      const { data: blob } = await api.get(
        `/seller/marketplace/myntra/orders/export.csv${qs ? `?${qs}` : ""}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement("a");
      a.href = url;
      const dateSlug = dateFrom || dateTo ? `_${dateFrom || "start"}_to_${dateTo || "now"}` : "";
      a.download = `myntra_orders${dateSlug}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Orders CSV exported");
    } catch (err) {
      toast.error(errMsg(err, "Export failed"));
    }
  }

  function setQuickRange(days) {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days + 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    setDateFrom(iso(from));
    setDateTo(iso(to));
  }

  return (
    <div className="space-y-3" data-testid="orders-tab">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search order ID…"
          className="px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-xs text-white placeholder:text-white/40 focus:outline-none focus:border-[#4F46E5]"
          data-testid="orders-search"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-xs text-white focus:outline-none focus:border-[#4F46E5]"
          data-testid="orders-status-filter"
        >
          <option value="">All statuses</option>
          <option value="created">Created</option>
          <option value="acknowledged">Accepted</option>
          <option value="packed">Packed</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08]">
          <span className="text-[10px] text-white/45 uppercase tracking-widest font-bold">From</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-transparent text-xs text-white focus:outline-none [color-scheme:dark]"
            data-testid="orders-date-from"
          />
          <span className="text-[10px] text-white/45 uppercase tracking-widest font-bold ml-1">To</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-transparent text-xs text-white focus:outline-none [color-scheme:dark]"
            data-testid="orders-date-to"
          />
        </div>
        {["7", "30", "90"].map((d) => (
          <button
            key={d}
            onClick={() => setQuickRange(parseInt(d, 10))}
            className="text-[10px] font-bold uppercase tracking-widest text-white/60 hover:text-white border border-white/[0.08] hover:border-white/[0.25] px-2 py-1 rounded"
            data-testid={`orders-quick-${d}d`}
          >
            {d}d
          </button>
        ))}
        {(dateFrom || dateTo) && (
          <button
            onClick={() => { setDateFrom(""); setDateTo(""); }}
            className="text-[10px] font-bold text-rose-300 hover:text-rose-100"
            data-testid="orders-clear-dates"
          >
            Clear dates
          </button>
        )}
        <button
          onClick={onExportCsv}
          className="px-3 py-1.5 rounded-md border border-emerald-400/30 bg-emerald-500/[0.06] text-xs font-bold text-emerald-200 hover:bg-emerald-500/[0.15] transition-colors ml-auto flex items-center gap-1.5"
          data-testid="orders-export-csv"
        >
          Download CSV
        </button>
      </div>

      {filtered.length === 0 ? (
        <Empty msg={orders.length === 0 ? "No orders mirrored yet. They'll appear here as soon as Myntra fires a create-order webhook." : "No orders match your filter."} />
      ) : (
        <div className="space-y-2">
          {filtered.map((o) => (
            <OrderRow
              key={o.seller_order_id}
              order={o}
              busy={busySku && busySku.startsWith(`${o.seller_order_id}:`) ? busySku.split(":")[1] : null}
              onAction={(a, extra) => runAction(o.seller_order_id, a, extra)}
              onPdf={(kind) => openPdf(o.seller_order_id, kind)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OrderRow({ order, busy, onAction, onPdf }) {
  const s = (order.status || order.pipeline_stage || "").toLowerCase();
  const canAccept = ["created", "pending", "new"].includes(s);
  const canPack = ["acknowledged", "accepted"].includes(s);
  const canShip = ["packed", "ready_to_dispatch"].includes(s);
  const canCancel = !["shipped", "delivered", "cancelled", "returned"].includes(s);
  const [showShip, setShowShip] = useState(false);
  const [tracking, setTracking] = useState("");

  return (
    <div
      className="p-3 rounded-lg bg-white/[0.025] border border-white/10"
      data-testid={`myntra-order-${order.seller_order_id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-bold text-white font-mono">{order.seller_order_id}</span>
            <StatusPill value={order.status} />
            {order.pipeline_stage && (
              <span className="text-[9px] uppercase tracking-widest text-white/45 font-bold">→ {order.pipeline_stage}</span>
            )}
          </div>
          <div className="text-[11px] text-white/45 font-mono">
            {order.customer_name ? `${order.customer_name} · ` : ""}
            {order.sku ? `${order.sku} × ${order.quantity || 1} · ` : ""}
            {order.total_amount ? `₹${Number(order.total_amount).toLocaleString("en-IN")} · ` : ""}
            {order.first_seen_at || order.created_at}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {canAccept && (
            <ActionBtn
              testId={`order-accept-${order.seller_order_id}`}
              label={busy === "accept" ? "…" : "Accept"}
              tone="sky"
              onClick={() => onAction("accept")}
              disabled={!!busy}
            />
          )}
          {canPack && (
            <ActionBtn
              testId={`order-pack-${order.seller_order_id}`}
              label={busy === "pack" ? "…" : "Pack"}
              tone="violet"
              onClick={() => onAction("pack")}
              disabled={!!busy}
            />
          )}
          {canShip && !showShip && (
            <ActionBtn
              testId={`order-ship-${order.seller_order_id}`}
              label="Ship"
              tone="emerald"
              onClick={() => setShowShip(true)}
              disabled={!!busy}
            />
          )}
          <ActionBtn
            testId={`order-invoice-${order.seller_order_id}`}
            label="Invoice"
            tone="amber"
            onClick={() => onPdf("invoice")}
            outline
          />
          <ActionBtn
            testId={`order-label-${order.seller_order_id}`}
            label="Label"
            tone="rose"
            onClick={() => onPdf("label")}
            outline
          />
          {canCancel && (
            <ActionBtn
              testId={`order-cancel-${order.seller_order_id}`}
              label="Cancel"
              tone="rose"
              onClick={() => {
                const reason = window.prompt("Cancellation reason?", "seller_cancel");
                if (reason !== null) onAction("cancel", { reason });
              }}
              outline
              disabled={!!busy}
            />
          )}
        </div>
      </div>
      {showShip && (
        <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center gap-2" data-testid={`order-ship-form-${order.seller_order_id}`}>
          <input
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            placeholder="AWB / tracking number"
            className="flex-1 px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-xs text-white placeholder:text-white/40 focus:outline-none focus:border-emerald-400/60 font-mono"
            data-testid={`order-tracking-input-${order.seller_order_id}`}
          />
          <button
            onClick={() => { if (tracking.trim()) { onAction("ship", { tracking_number: tracking.trim() }); setShowShip(false); setTracking(""); } }}
            disabled={!tracking.trim() || !!busy}
            className="px-3 py-1.5 rounded-md bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 text-xs font-bold hover:bg-emerald-500/30 disabled:opacity-40"
            data-testid={`order-ship-confirm-${order.seller_order_id}`}
          >
            {busy === "ship" ? "Shipping…" : "Confirm ship"}
          </button>
          <button onClick={() => setShowShip(false)} className="text-white/50 hover:text-white text-xs px-2">Cancel</button>
        </div>
      )}
      {order.events?.length > 0 && (
        <div className="mt-2 flex items-center gap-1 flex-wrap">
          {order.events.slice(0, 6).map((ev, i) => (
            <span
              key={i}
              className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/[0.04] text-white/60 font-mono"
              title={ev.received_at}
            >
              {ev.event_type || ev.status || "event"}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ label, tone = "sky", onClick, disabled, outline, testId }) {
  const tones = {
    sky:     outline ? "text-sky-300 border-sky-400/30 hover:bg-sky-500/10" : "bg-sky-500/20 text-sky-200 border-sky-400/30 hover:bg-sky-500/30",
    violet:  outline ? "text-indigo-300 border-indigo-400/30 hover:bg-indigo-500/10" : "bg-indigo-500/20 text-indigo-200 border-indigo-400/30 hover:bg-indigo-500/30",
    emerald: outline ? "text-emerald-300 border-emerald-400/30 hover:bg-emerald-500/10" : "bg-emerald-500/20 text-emerald-200 border-emerald-400/30 hover:bg-emerald-500/30",
    amber:   outline ? "text-amber-300 border-amber-400/30 hover:bg-amber-500/10" : "bg-amber-500/20 text-amber-200 border-amber-400/30 hover:bg-amber-500/30",
    rose:    outline ? "text-rose-300 border-rose-400/30 hover:bg-rose-500/10" : "bg-rose-500/20 text-rose-200 border-rose-400/30 hover:bg-rose-500/30",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded border transition-colors disabled:opacity-40 ${tones[tone] || tones.sky}`}
    >
      {label}
    </button>
  );
}

function ReturnsTab({ returns }) {
  if (!returns.length) return <Empty msg="No returns yet — return webhooks land here when Myntra creates RTO/RTV events." />;
  return (
    <div className="space-y-2">
      {returns.map((r) => (
        <div key={r.return_id} className="p-3 rounded-lg bg-white/[0.025] border border-white/10 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold text-white font-mono">{r.return_id}</span>
              <StatusPill value={r.status} />
            </div>
            <div className="text-[11px] text-white/45 font-mono">
              first seen {r.first_seen_at}
              {r.events?.length ? `  ·  ${r.events.length} events` : ""}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EventsTab({ events }) {
  if (!events.length) return <Empty msg="No webhook events received yet." />;
  return (
    <div className="space-y-2">
      {events.map((e) => (
        <div key={e.id} className="p-3 rounded-lg bg-white/[0.025] border border-white/10">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] px-2 py-0.5 rounded-md font-bold bg-[#4F46E5]/15 text-[#A5B4FC] uppercase tracking-wider">
                {e.event_type}
              </span>
              <span className="text-[10px] text-white/45 font-mono">{e.received_at}</span>
            </div>
            <PackageCheck size={12} className="text-emerald-400" />
          </div>
          <pre className="text-[10px] text-white/60 font-mono whitespace-pre-wrap line-clamp-4 mt-1">
            {JSON.stringify(e.body, null, 2).slice(0, 600)}
          </pre>
        </div>
      ))}
    </div>
  );
}

function ActionsTab() {
  const ACTIONS = [
    { label: "Update inventory",   path: "POST  /api/seller/myntra/inventory/update", desc: "Push current stock levels to Myntra" },
    { label: "Search inventory",   path: "POST  /api/seller/myntra/inventory/search", desc: "Fetch live inventory from Myntra" },
    { label: "Upsert SKU",         path: "POST  /api/seller/myntra/sku/upsert",       desc: "Add or update SKUs on Myntra" },
    { label: "Get order",          path: "GET   /api/seller/myntra/order/{id}",       desc: "Pull a live order from Myntra" },
    { label: "Accept order",       path: "POST  /api/seller/myntra/order/{id}/accept",desc: "Acknowledge an order" },
    { label: "Reject order",       path: "POST  /api/seller/myntra/order/{id}/reject",desc: "Reject an order with a reason" },
  ];
  return (
    <div className="space-y-2">
      <div className="text-xs text-white/55 mb-2">
        These outbound endpoints wrap the Mockify contract. Each call auto-refreshes the access token on 477.
      </div>
      {ACTIONS.map((a) => (
        <div key={a.label} className="p-3 rounded-lg bg-white/[0.025] border border-white/10">
          <div className="text-sm font-bold text-white">{a.label}</div>
          <div className="text-[10px] font-mono text-[#A5B4FC] mt-0.5">{a.path}</div>
          <div className="text-[11px] text-white/55 mt-1">{a.desc}</div>
        </div>
      ))}
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="p-8 rounded-xl bg-white/[0.02] border border-white/[0.06] text-center">
      <PackageOpen size={24} className="text-white/30 mx-auto mb-2" />
      <div className="text-sm text-white/55">{msg}</div>
    </div>
  );
}


// ═══════════ Sync Dashboard ═══════════════════════════════════════════
const SYNC_STATUS_TONE = {
  idle:    "bg-slate-500/15 text-slate-300 border-slate-500/30",
  running: "bg-sky-500/15 text-sky-300 border-sky-500/30 animate-pulse",
  success: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  failed:  "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

const TOKEN_STATUS_TONE = {
  healthy:        { cls: "text-emerald-300", label: "Healthy" },
  expiring_soon:  { cls: "text-amber-300", label: "Expiring soon" },
  expired:        { cls: "text-rose-300", label: "Expired" },
  missing:        { cls: "text-white/50", label: "Not connected" },
  unknown:        { cls: "text-white/50", label: "Unknown" },
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function SyncTab({ data, logs, onRetry }) {
  if (!data) return <Empty msg="Sync status unavailable" />;
  const { connection, syncs, config } = data;
  const notConnected = !connection?.connected || connection?.token_status === "missing";
  const tokenTone = TOKEN_STATUS_TONE[connection?.token_status] || TOKEN_STATUS_TONE.unknown;
  return (
    <div className="space-y-5">
      {/* Prominent CTA when Myntra is not connected — sets the right
          expectation instead of showing scary red "FAILED" pills for
          channels that can never succeed without credentials. */}
      {notConnected && (
        <div
          className="p-4 rounded-xl border border-indigo-400/30 bg-indigo-500/[0.06] flex items-start gap-3"
          data-testid="sync-not-connected-cta"
        >
          <Link2 size={18} className="text-indigo-300 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-bold text-white">Connect Myntra to start syncing</div>
            <div className="text-[12px] text-white/60 mt-1">
              Add your merchant credentials from <strong>Profile → Myntra Partner API</strong> to
              turn on live orders, inventory sync and one-click listing publishes. Until then
              the channels below will show as pending.
            </div>
          </div>
          <Link
            to="/seller/profile"
            className="shrink-0 self-center px-3 py-1.5 rounded-md bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white text-xs font-bold whitespace-nowrap"
            data-testid="sync-connect-cta"
          >
            Connect now →
          </Link>
        </div>
      )}
      {/* Connection + Token Health */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.08]" data-testid="sync-connection-card">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold flex items-center gap-1.5">
            <Link2 size={11} /> Connection status
          </div>
          <div className={`text-lg font-bold mt-1.5 ${connection?.connected ? "text-emerald-300" : "text-rose-300"}`}>
            {connection?.connected ? "Connected" : "Not connected"}
          </div>
          {connection?.merchant_id && (
            <div className="text-[11px] font-mono text-white/50 mt-0.5">
              Merchant: {connection.merchant_id}
            </div>
          )}
        </div>
        <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.08]" data-testid="sync-token-card">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold flex items-center gap-1.5">
            <ShieldCheckIcon /> Token status
          </div>
          <div className={`text-lg font-bold mt-1.5 ${tokenTone.cls}`}>{tokenTone.label}</div>
          <div className="text-[11px] text-white/50 mt-0.5">
            {connection?.token_expires_at
              ? `Expires ${new Date(connection.token_expires_at).toLocaleString()}`
              : "—"}
          </div>
        </div>
        <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.08]" data-testid="sync-mode-card">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold flex items-center gap-1.5">
            <Activity size={11} /> Environment
          </div>
          <div className={`text-lg font-bold mt-1.5 ${config?.mode === "production" ? "text-emerald-300" : "text-amber-300"}`}>
            {config?.mode === "production" ? "Production" : "Mockify (sandbox)"}
          </div>
          <div className="text-[11px] font-mono text-white/50 mt-0.5 truncate" title={config?.base_url}>
            {config?.base_url}
          </div>
        </div>
      </div>

      {/* 4 sync channels */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-2 flex items-center gap-1.5">
          <ListChecks size={12} /> Sync channels
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(syncs || []).map((s) => (
            <div
              key={s.sync_type}
              className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.08]"
              data-testid={`sync-channel-${s.sync_type}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50 font-bold">
                    {s.sync_type}
                  </div>
                  <div className="mt-1.5">
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-md font-bold border uppercase tracking-wider ${SYNC_STATUS_TONE[s.status] || SYNC_STATUS_TONE.idle}`}
                      data-testid={`sync-status-badge-${s.sync_type}`}
                    >
                      {s.status}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => onRetry(s.sync_type)}
                  className="text-[10px] uppercase tracking-widest font-bold text-indigo-300 hover:text-indigo-100 border border-indigo-400/30 hover:border-indigo-400 px-2.5 py-1 rounded-md flex items-center gap-1.5 transition-colors"
                  data-testid={`retry-sync-${s.sync_type}`}
                >
                  <RefreshCcw size={11} /> Retry
                </button>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <div className="text-white/40 uppercase tracking-wider text-[9px] font-bold">Last sync</div>
                  <div className="text-white/80 mt-0.5">{fmtTime(s.last_sync_at)}</div>
                </div>
                <div>
                  <div className="text-white/40 uppercase tracking-wider text-[9px] font-bold">Last success</div>
                  <div className="text-white/80 mt-0.5">{fmtTime(s.last_success_at)}</div>
                </div>
              </div>
              {s.last_error && (
                <div className="mt-2 text-[11px] text-rose-300 flex items-start gap-1.5" data-testid={`sync-error-${s.sync_type}`}>
                  <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                  <span className="truncate">{s.last_error}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Recent API logs */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-2 flex items-center gap-1.5">
          <AlertTriangle size={12} /> Recent API activity ({logs?.length || 0})
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
          {(logs || []).length === 0 ? (
            <div className="p-6 text-center text-[12px] text-white/40">
              No API calls yet — start syncing to populate this log.
            </div>
          ) : (
            <div className="divide-y divide-white/[0.05] max-h-80 overflow-y-auto">
              {(logs || []).map((l) => (
                <div key={l.id} className="px-4 py-2.5 flex items-start gap-3" data-testid={`api-log-${l.id}`}>
                  <span
                    className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${
                      l.status_code >= 400
                        ? "bg-rose-500/20 text-rose-300"
                        : l.status_code >= 200
                        ? "bg-emerald-500/20 text-emerald-300"
                        : "bg-slate-500/20 text-slate-300"
                    }`}
                  >
                    {l.status_code || "—"}
                  </span>
                  <span className="text-[10px] font-mono font-bold text-indigo-300 shrink-0">
                    {l.method}
                  </span>
                  <span className="text-[11px] font-mono text-white/75 truncate flex-1">
                    {l.endpoint}
                  </span>
                  <span className="text-[10px] text-white/40 shrink-0">
                    {l.duration_ms}ms · {fmtTime(l.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ShieldCheckIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

// ═══════════ Warehouses (assigned to this seller) ═════════════════════
function WarehousesTab({ warehouses }) {
  if (!warehouses || warehouses.length === 0) {
    return (
      <div className="p-8 rounded-xl bg-white/[0.02] border border-white/[0.06] text-center" data-testid="warehouses-empty">
        <WarehouseIcon size={24} className="text-white/30 mx-auto mb-2" />
        <div className="text-sm text-white/55">No warehouses assigned yet</div>
        <div className="text-xs text-white/40 mt-1">
          Ask your account manager to assign a warehouse code from the Admin console.
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="warehouses-tab">
      <div className="text-xs text-white/55">
        These are the Myntra warehouse codes assigned to your account. Use them when
        publishing SKUs or pushing inventory updates.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {warehouses.map((w) => (
          <div
            key={w.id}
            className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.08]"
            data-testid={`seller-warehouse-${w.code}`}
          >
            <div className="flex items-center gap-2">
              <WarehouseIcon size={14} className="text-indigo-300" />
              <div className="font-mono text-sm font-bold text-white">{w.code}</div>
              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-bold ml-auto">
                Active
              </span>
            </div>
            <div className="text-sm text-white/80 mt-1">{w.name}</div>
            <div className="text-[11px] text-white/50 mt-1">
              {[w.city, w.state, w.pincode].filter(Boolean).join(" · ") || "—"}
              {w.gstin && <> · GSTIN {w.gstin}</>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════ Dashboard tiles (P5) ═════════════════════════════════════
function DashboardTab({ tiles }) {
  if (!tiles) return <Empty msg="Loading dashboard…" />;
  const health = tiles.api_health || {};
  const healthy = health.token_status === "healthy";
  const syncSummary = tiles.sync_summary || {};
  const rupees = (v) =>
    "₹" + (Number(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  return (
    <div className="space-y-5" data-testid="myntra-dashboard-tab">
      {/* Top 4 KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          icon={ShoppingBag}
          label="Today's orders"
          value={tiles.orders_today}
          tone="violet"
          testId="tile-orders-today"
        />
        <Tile
          icon={IndianRupee}
          label="Today's revenue"
          value={rupees(tiles.revenue_today)}
          tone="emerald"
          testId="tile-revenue-today"
        />
        <Tile
          icon={Clock}
          label="Pending orders"
          value={tiles.pending_orders}
          tone="amber"
          testId="tile-pending-orders"
        />
        <Tile
          icon={RotateCcw}
          label="Returns today"
          value={tiles.returns_today}
          tone="rose"
          sub={`${tiles.returns_total || 0} total`}
          testId="tile-returns-today"
        />
      </div>

      {/* Bottom 4 KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          icon={AlertTriangle}
          label="Inventory alerts"
          value={tiles.inventory_alerts}
          tone={tiles.inventory_alerts > 0 ? "rose" : "sky"}
          testId="tile-inventory-alerts"
        />
        <Tile
          icon={PackagePlus}
          label="Listings published"
          value={tiles.listings_published}
          tone="sky"
          testId="tile-listings-published"
        />
        <Tile
          icon={healthy ? CheckCircle2 : XCircle}
          label="API health"
          value={healthy ? "Healthy" : health.token_status === "missing" ? "Not connected" : (health.token_status || "Unknown")}
          tone={healthy ? "emerald" : "rose"}
          testId="tile-api-health"
        />
        <Tile
          icon={Gauge}
          label="Last sync"
          value={tiles.last_sync_at ? new Date(tiles.last_sync_at).toLocaleTimeString() : "—"}
          tone="violet"
          sub={tiles.config_mode === "production" ? "Production" : "Sandbox"}
          testId="tile-last-sync"
        />
      </div>

      {/* Sync channel mini-summary */}
      <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.08]">
        <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2 flex items-center gap-1.5">
          <ListChecks size={11} /> Sync channels
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {["listings", "inventory", "orders", "returns"].map((k) => {
            const st = syncSummary[k] || "idle";
            return (
              <div
                key={k}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.05]"
                data-testid={`dash-sync-${k}`}
              >
                <span className="text-xs uppercase tracking-widest text-white/60 font-bold">{k}</span>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded font-bold border uppercase tracking-wider ${SYNC_STATUS_TONE[st] || SYNC_STATUS_TONE.idle}`}
                >
                  {st}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, tone = "violet", sub, testId }) {
  const toneCls = {
    violet: "text-[#A5B4FC]",
    emerald: "text-emerald-300",
    rose: "text-rose-300",
    amber: "text-amber-300",
    sky: "text-sky-300",
  }[tone] || "text-white";
  return (
    <div className="p-4 rounded-xl bg-white/[0.025] border border-white/10" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold flex items-center gap-1.5">
        <Icon size={11} className={toneCls} /> {label}
      </div>
      <div className={`text-2xl font-bold mt-1 ${toneCls} tabular-nums`}>{value}</div>
      {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
    </div>
  );
}


// ═══════════ Listings (per-SKU publish — P2 wiring) ═══════════════════
const LIVE_STATUS_TONE = {
  live:    "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  pending: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  staged:  "bg-amber-500/15 text-amber-300 border-amber-500/30",
  failed:  "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

function ListingsTab({ listings, onPublish, onRefresh }) {
  if (!listings || listings.length === 0) {
    return (
      <Empty msg="No listings yet. Generate an Excel via AI Auto-Listing and push to Myntra — SKUs appear here with per-row status." />
    );
  }
  return (
    <div className="space-y-2" data-testid="myntra-listings-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-white/55">
          Per-SKU listing state. Failed SKUs show the Myntra rejection reason — use <strong className="text-fuchsia-300">Fix with AI</strong> to auto-regenerate and resubmit.
        </div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/45">
          <span className="px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/15 text-emerald-300 font-bold">
            {listings.filter((l) => l.live_status === "live").length} Live
          </span>
          <span className="px-1.5 py-0.5 rounded border border-rose-500/30 bg-rose-500/15 text-rose-300 font-bold">
            {listings.filter((l) => l.live_status === "failed").length} Failed
          </span>
        </div>
      </div>
      <div className="space-y-2">
        {listings.map((l) => (
          <ListingRow key={l.sku} listing={l} onPublish={onPublish} onRefresh={onRefresh} />
        ))}
      </div>
    </div>
  );
}

function ListingRow({ listing, onPublish, onRefresh }) {
  const l = listing;
  const tone = LIVE_STATUS_TONE[l.live_status] || LIVE_STATUS_TONE.staged;
  const canPublish = l.live_status !== "live";
  const isFailed = l.live_status === "failed";
  const [expanded, setExpanded] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  const rejectionsParsed = useMemo(() => {
    const raw = l.rejection_payload || l.last_error;
    if (!raw) return {};
    if (typeof raw === "object") {
      // Try common shapes
      if (raw.errors && Array.isArray(raw.errors)) {
        const out = {};
        raw.errors.forEach((e) => {
          if (e && (e.field || e.attribute)) out[e.field || e.attribute] = e.message || e.reason || "";
        });
        return out;
      }
      return raw;
    }
    return { _error: String(raw).slice(0, 400) };
  }, [l.rejection_payload, l.last_error]);

  async function onFixWithAI(resubmit = true) {
    setFixing(true);
    try {
      const { data } = await api.post(
        `/seller/marketplace/myntra/listing/${encodeURIComponent(l.sku)}/fix-with-ai`,
        { resubmit }
      );
      setFixResult(data);
      const attrCount = Object.keys(data.fixed_attrs || {}).length;
      if (data.resubmitted) {
        toast.success(`Fixed ${attrCount} attr(s) → ${data.resubmit_status}`);
      } else if (data.resubmit_error) {
        toast.error(`Fix succeeded but resubmit failed: ${data.resubmit_error}`);
      } else {
        toast.success(`Regenerated ${attrCount} attr(s). Click Publish to resubmit.`);
      }
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(errMsg(err, "AI fix failed"));
    } finally {
      setFixing(false);
    }
  }

  return (
    <div
      className={`rounded-xl border ${isFailed ? "border-rose-400/25 bg-rose-500/[0.04]" : "border-white/[0.08] bg-white/[0.02]"} overflow-hidden`}
      data-testid={`listing-row-${l.sku}`}
    >
      <div className="grid grid-cols-12 gap-3 px-4 py-3 items-center">
        <div className="col-span-3 font-mono text-sm font-bold text-white truncate">{l.sku}</div>
        <div className="col-span-2 text-[11px] font-mono text-white/70">{l.warehouse_code || "—"}</div>
        <div className="col-span-2 text-[11px] text-white/70 tabular-nums">
          {l.price ? `₹${Number(l.price).toLocaleString("en-IN")}` : "—"}
        </div>
        <div className="col-span-2">
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded font-bold border uppercase tracking-wider ${tone}`}
            data-testid={`listing-status-${l.sku}`}
          >
            {l.live_status || "staged"}
          </span>
        </div>
        <div className="col-span-3 flex items-center justify-end gap-1.5">
          {isFailed && (
            <button
              onClick={() => setExpanded((x) => !x)}
              className="text-[10px] uppercase tracking-widest font-bold text-rose-300 hover:text-white border border-rose-400/30 px-2 py-1 rounded"
              data-testid={`listing-reject-toggle-${l.sku}`}
            >
              {expanded ? "Hide" : `Why?`}
            </button>
          )}
          {isFailed && (
            <button
              onClick={() => onFixWithAI(true)}
              disabled={fixing}
              className="text-[10px] uppercase tracking-widest font-bold text-white bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:from-fuchsia-400 hover:to-indigo-400 px-2.5 py-1 rounded flex items-center gap-1 disabled:opacity-50"
              data-testid={`listing-fix-ai-${l.sku}`}
            >
              {fixing ? "Fixing…" : "✨ Fix with AI"}
            </button>
          )}
          {canPublish && !isFailed && (
            <button
              onClick={() => onPublish(l)}
              className="text-[10px] uppercase tracking-widest font-bold text-indigo-300 hover:text-indigo-100 border border-indigo-400/30 hover:border-indigo-400 px-2 py-1 rounded"
              data-testid={`publish-sku-${l.sku}`}
            >
              Publish
            </button>
          )}
          {!canPublish && (
            <CheckCircle2 size={13} className="text-emerald-300" />
          )}
        </div>
      </div>
      {expanded && isFailed && (
        <div className="border-t border-rose-400/20 bg-rose-500/[0.03] px-4 py-3 space-y-2" data-testid={`listing-rejection-${l.sku}`}>
          <div className="text-[10px] uppercase tracking-widest text-rose-300 font-bold flex items-center gap-1.5">
            <AlertTriangle size={11} /> Myntra rejection reasons
          </div>
          {Object.keys(rejectionsParsed).length === 0 ? (
            <div className="text-[12px] text-white/60">No structured reason parsed. Raw error: {String(l.last_error || "").slice(0, 200)}</div>
          ) : (
            <div className="space-y-1">
              {Object.entries(rejectionsParsed).slice(0, 8).map(([field, reason]) => (
                <div key={field} className="flex items-start gap-2 text-[11.5px]">
                  <span className="font-mono font-bold text-rose-200 min-w-[100px]">{field}</span>
                  <span className="text-white/70">{String(reason)}</span>
                </div>
              ))}
            </div>
          )}
          {fixResult && (
            <div className="mt-3 pt-3 border-t border-white/[0.06] space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">
                AI regeneration ({fixResult.source})
              </div>
              {Object.entries(fixResult.fixed_attrs || {}).map(([k, v]) => (
                <div key={k} className="text-[11.5px] flex items-start gap-2">
                  <span className="font-mono font-bold text-fuchsia-200 min-w-[100px]">{k}</span>
                  <span className="text-white/85">{String(v)}</span>
                </div>
              ))}
              {fixResult.resubmitted && (
                <div className="text-[11px] text-emerald-300 font-bold mt-1">✓ Resubmitted to Myntra · status: {fixResult.resubmit_status}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
